import { useState, useRef } from 'react';
import {
  ChevronDown, ChevronRight, Layers, X, Leaf, Mountain, TreePine,
  Building2, Plane, Shield, Globe2,
} from 'lucide-react';

// localStorage key — keeps the last-expanded country between panel open/close
// and across full page reloads. Avoids the perception of "scrolling back to
// France" every time the user re-opens the panel.
const EXPANDED_KEY = 'dm.layersPanel.expanded';

// --------------------------------------------------------------------------
// Stable sub-components (declared OUTSIDE MapLayersPanel on purpose).
//
// Defining these inside the parent component would give them a new function
// identity on every render. React would then unmount & remount the whole
// subtree each time any sibling state changed (e.g. ticking a checkbox),
// which destroys the scroll position of the panel body. Keeping them at
// module scope guarantees DOM stability across parent re-renders.
// --------------------------------------------------------------------------

const Row = ({ checked, onChange, color, label, sub, testId }) => (
  <label
    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
    data-testid={testId}
  >
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-3.5 h-3.5 accent-sky-500 flex-shrink-0"
    />
    <span
      className="w-3 h-3 rounded-sm flex-shrink-0 border"
      style={typeof color === 'string' ? { backgroundColor: color, borderColor: color } : color}
    />
    <span className="text-xs text-slate-700 flex-1 leading-tight">{label}</span>
    {sub && <span className="text-[10px] text-slate-400">{sub}</span>}
  </label>
);

const Section = ({ id, flag, name, active, total, expandedId, onToggle, children }) => {
  const isOpenSection = expandedId === id;
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 transition-colors"
        data-testid={`layers-section-${id}`}
      >
        {isOpenSection ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        {flag && <span className="text-base leading-none">{flag}</span>}
        <span className="text-sm font-semibold text-slate-800 flex-1 text-left">{name}</span>
        {active > 0 && (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-sky-100 text-sky-700">
            {active}{total ? `/${total}` : ''}
          </span>
        )}
      </button>
      {isOpenSection && <div className="pl-3 pb-2">{children}</div>}
    </div>
  );
};

const Ed269Filter = ({ cc, ed269Filter, setEd269Filter }) => {
  if (!setEd269Filter) return null;
  const opts = [
    { key: 'aero',      label: 'Aéronautique', hint: 'Aéroports uniquement' },
    { key: 'important', label: 'Important',   hint: 'PROHIBITED + REQ_AUTHORISATION' },
    { key: 'all',       label: 'Tout',        hint: 'Toutes catégories (peut être lourd)' },
  ];
  return (
    <div className="flex items-stretch gap-0 mx-2 my-1 rounded-md overflow-hidden border border-slate-200 bg-slate-50" data-testid={`ed269-filter-${cc}`}>
      {opts.map((o, i) => (
        <button
          key={o.key}
          type="button"
          title={o.hint}
          onClick={() => setEd269Filter(o.key)}
          className={`flex-1 px-2 py-1 text-[10px] font-medium transition-colors ${
            ed269Filter === o.key
              ? 'bg-sky-500 text-white'
              : 'text-slate-600 hover:bg-slate-100'
          } ${i > 0 ? 'border-l border-slate-200' : ''}`}
          data-testid={`ed269-filter-${cc}-${o.key}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

const Ed269Row = ({
  cc, count, active, loading,
  toggleEd269Country, showEd269Layer, setShowEd269Layer,
  ed269Filter, setEd269Filter,
}) => (
  <>
    <Row
      label={
        <span>
          Zones officielles <span className="text-[10px] font-mono text-slate-400">Drone Maps</span>
        </span>
      }
      sub={
        loading
          ? '⏳'
          : count != null
          ? `${count.toLocaleString('fr-FR')} zones`
          : <span className="text-slate-400">—</span>
        }
      checked={active}
      onChange={(v) => {
        // Multi-country selection: toggling a row only adds/removes that
        // specific country from the active Set. The global ED-269 master
        // switch is auto-enabled when the first country is turned on so
        // the user never has to think about it.
        toggleEd269Country(cc);
        if (v && !showEd269Layer) setShowEd269Layer(true);
      }}
      color={{ background: 'linear-gradient(135deg, #dc2626, #ea580c, #eab308)', borderColor: '#991b1b' }}
      testId={`layer-toggle-ed269-${cc}`}
    />
    {active && (
      <Ed269Filter cc={cc} ed269Filter={ed269Filter} setEd269Filter={setEd269Filter} />
    )}
  </>
);

/**
 * Floating accordion panel listing all map layers grouped by country/category.
 *
 * Inspired by FlyBy's compact right-side overlay: keeps the map area free of
 * dense toggle rows while still exposing every layer (WMS, ED-269, OpenAIP,
 * environment, POI, airspace).
 *
 * The component is purely presentational — it receives every state and setter
 * from the parent <FlightMap> and never touches Leaflet directly.
 */
export default function MapLayersPanel({
  isOpen,
  onClose,
  // Country WMS toggles
  showIgnLayer, setShowIgnLayer,
  showDipulLayer, setShowDipulLayer,
  showDfsAirspaceLayer, setShowDfsAirspaceLayer,
  showBelgiumLayer, setShowBelgiumLayer,
  showLuxembourgLayer, setShowLuxembourgLayer,
  showNetherlandsLayer, setShowNetherlandsLayer,
  showSwitzerlandLayer, setShowSwitzerlandLayer,
  showAustriaLayer, setShowAustriaLayer,
  showIcelandLayer, setShowIcelandLayer,
  showCzechiaLayer, setShowCzechiaLayer,
  showSlovakiaLayer, setShowSlovakiaLayer,
  showDenmarkLayer, setShowDenmarkLayer,
  showIrelandLayer, setShowIrelandLayer,
  showEstoniaLayer, setShowEstoniaLayer,
  showCyprusLayer, setShowCyprusLayer,
  showItalyLayer, setShowItalyLayer,
  showItalyDroneRestrictions, setShowItalyDroneRestrictions,
  showSwedenDroneRestrictions, setShowSwedenDroneRestrictions,
  showFinlandDroneRestrictions, setShowFinlandDroneRestrictions,
  showBelgiumDroneRestrictions, setShowBelgiumDroneRestrictions,
  showNetherlandsDroneRestrictions, setShowNetherlandsDroneRestrictions,
  showPolandDroneRestrictions, setShowPolandDroneRestrictions,
  showNorwayDroneRestrictions, setShowNorwayDroneRestrictions,
  showCroatiaDroneRestrictions, setShowCroatiaDroneRestrictions,
  showGreeceDroneRestrictions, setShowGreeceDroneRestrictions,
  showBulgariaDroneRestrictions, setShowBulgariaDroneRestrictions,
  showHungaryDroneRestrictions, setShowHungaryDroneRestrictions,
  showSloveniaDroneRestrictions, setShowSloveniaDroneRestrictions,
  showSpainLayer, setShowSpainLayer,
  // OpenAIP & global
  showOpenAipLayer, setShowOpenAipLayer,
  showDjiLayer, setShowDjiLayer,
  // NOTAM overlay — first-class layer per user feedback ("un pro sans NOTAMs
  // a une carte incomplète"). Renders as a floating card, not a Leaflet
  // layer, so we just need the state pair here.
  showNotamsLayer, setShowNotamsLayer,
  // ED-269
  showEd269Layer, setShowEd269Layer,
  ed269Countries, toggleEd269Country, setEd269Country,
  ed269Filter, setEd269Filter,
  ed269Available = [], ed269Loading,
  // OpenAIP airspace breakdown (CTR/TMA/P/R/D for France)
  layerVisibility, setLayerVisibility,
  // Environment
  showNatura2000Wms, setShowNatura2000Wms,
  showEnvironmentalLayers, setShowEnvironmentalLayers,
  loadingEnvZones,
  // POI
  showHospitalsLayer, setShowHospitalsLayer,
  showAirportsLayer, setShowAirportsLayer,
  showAirportsPPPLayer, setShowAirportsPPPLayer,
  showHelistationsLayer, setShowHelistationsLayer,
}) {
  // Single-expanded-section accordion: only one country/group is expanded at
  // a time. This keeps the panel compact (no need to scroll back up after
  // exploring another country) and removes the bug where the active country
  // disappeared under a tall, always-open France block.
  // The last opened section is persisted to localStorage so re-opening the
  // panel restores the user's last choice instead of forcing France back to
  // the top every time.
  const initialExpanded = (() => {
    try {
      const saved = typeof window !== 'undefined' && window.localStorage?.getItem(EXPANDED_KEY);
      if (saved) return saved;
    } catch (_) { /* ignore SSR / private mode */ }
    return 'openaip';
  })();
  const [expandedId, setExpandedId] = useState(initialExpanded);

  // Ref on the scrollable body — used to scroll the freshly opened section
  // into view so the user always sees the toggles they just exposed.
  const bodyRef = useRef(null);

  const toggle = (key) => {
    setExpandedId((prev) => {
      const next = prev === key ? null : key;
      try {
        if (next) window.localStorage?.setItem(EXPANDED_KEY, next);
        else window.localStorage?.removeItem(EXPANDED_KEY);
      } catch (_) { /* ignore */ }
      // Scroll the newly opened section into view (or to top if collapsing).
      // We do this here — only on user-initiated toggle — instead of in an
      // effect tied to expandedId, otherwise any re-render that recreates
      // the body subtree (checkbox tick → parent re-render) would re-trigger
      // the scroll and visually "jump" the panel.
      if (next && bodyRef.current) {
        requestAnimationFrame(() => {
          const header = bodyRef.current?.querySelector(`[data-testid="layers-section-${next}"]`);
          if (header && bodyRef.current) {
            try { bodyRef.current.scrollTo({ top: header.offsetTop - 4, behavior: 'smooth' }); } catch (_) { /* ignore */ }
          }
        });
      }
      return next;
    });
  };

  // Convenience derived state: number of layers active per country (for badges)
  const countActive = (...vals) => vals.filter(Boolean).length;

  // ED-269 helper bound to a specific country.
  // Multi-country layer: every country whose code is in `ed269Countries`
  // (a Set) is currently displayed. Clicking a country's row toggles its
  // membership without affecting the other active countries.
  const ed269Lookup = Object.fromEntries(
    (ed269Available || []).map((c) => [c.country?.toLowerCase(), c.count])
  );
  const isEd269Active = (cc) => showEd269Layer && !!ed269Countries?.has?.(cc);

  // Bound props once so each <Ed269Row /> below only needs the country code.
  const ed269RowProps = {
    loading: ed269Loading,
    toggleEd269Country,
    showEd269Layer,
    setShowEd269Layer,
    ed269Filter,
    setEd269Filter,
  };

  // Same idea for <Section /> — binds the accordion state without forcing
  // every call site to repeat both props.
  const sectionProps = { expandedId, onToggle: toggle };

  if (!isOpen) return null;

  return (
    <div
      className="absolute top-3 right-3 bottom-3 z-[1000] w-80 bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
      data-testid="map-layers-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-sky-600" />
          <h3 className="text-sm font-semibold text-slate-900">Couches & données</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-slate-200 text-slate-500"
          data-testid="map-layers-close"
          aria-label="Close layers panel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Scrollable accordion body */}
      <div ref={bodyRef} className="overflow-y-auto flex-1 divide-y divide-slate-100">

        {/* NOTAMs — surfaced as the very first section because pros
            consider a map without active NOTAMs incomplete. Not tied to
            a specific country: fetched dynamically for whatever the map
            centre happens to be. */}
        <Section {...sectionProps} id="notams" flag="🛫" name="NOTAMs actifs" active={countActive(showNotamsLayer)}>
          <Row
            label="Afficher les NOTAMs de la zone visible"
            checked={showNotamsLayer}
            onChange={setShowNotamsLayer}
            color={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderColor: '#b45309' }}
            testId="layer-toggle-notams"
          />
          <p className="text-[11px] text-slate-500 mt-1 leading-snug">
            Rayon 100 km autour du centre de la carte · EUROCONTROL EAD · rafraîchi à chaque déplacement.
          </p>
        </Section>

        {/* European OpenAIP airspace — shown first as the cross-border default */}
        <Section {...sectionProps} id="openaip" flag="🇪🇺" name="Espaces aériens européens" active={countActive(showOpenAipLayer)}>
          <Row label="CTR, TMA, zones P/D/R" checked={showOpenAipLayer} onChange={setShowOpenAipLayer} color={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', borderColor: '#1e40af' }} testId="layer-toggle-openaip" />
        </Section>

        {/* France */}
        <Section
          {...sectionProps}
          id="france"
          flag="🇫🇷"
          name="France"
          active={countActive(showIgnLayer, layerVisibility?.ctr, layerVisibility?.tma, layerVisibility?.prohibited, layerVisibility?.restricted, layerVisibility?.danger)}
        >
          <Row label="Carte aéronautique officielle" checked={showIgnLayer} onChange={setShowIgnLayer} color={{ background: 'linear-gradient(135deg, #ff6b6b, #ffa500)', borderColor: '#e53e3e' }} testId="layer-toggle-ign" />
          <Row label="CTR — Zones de contrôle" checked={layerVisibility?.ctr} onChange={(v) => setLayerVisibility((p) => ({ ...p, ctr: v }))} color={{ backgroundColor: 'rgba(14, 165, 233, 0.4)', borderColor: '#0ea5e9' }} testId="layer-toggle-ctr" />
          <Row label="TMA — Terminal Areas" checked={layerVisibility?.tma} onChange={(v) => setLayerVisibility((p) => ({ ...p, tma: v }))} color={{ backgroundColor: 'rgba(139, 92, 246, 0.3)', borderColor: '#8b5cf6' }} testId="layer-toggle-tma" />
          <Row label="Zones P — Prohibited" checked={layerVisibility?.prohibited} onChange={(v) => setLayerVisibility((p) => ({ ...p, prohibited: v }))} color={{ backgroundColor: 'rgba(239, 68, 68, 0.4)', borderColor: '#ef4444' }} testId="layer-toggle-prohibited" />
          <Row label="Zones R — Restricted" checked={layerVisibility?.restricted} onChange={(v) => setLayerVisibility((p) => ({ ...p, restricted: v }))} color={{ backgroundColor: 'rgba(245, 158, 11, 0.4)', borderColor: '#f59e0b' }} testId="layer-toggle-restricted" />
          <Row label="Zones D — Danger" checked={layerVisibility?.danger} onChange={(v) => setLayerVisibility((p) => ({ ...p, danger: v }))} color={{ backgroundColor: 'rgba(249, 115, 22, 0.3)', borderColor: '#f97316' }} testId="layer-toggle-danger" />
          <Ed269Row cc="fr" count={ed269Lookup.fr} active={isEd269Active('fr')} {...ed269RowProps} />
                </Section>

        {/* Germany — Restrictions drones (DIPUL) sont désormais servies en
            vecteurs ED-318 via la sync backend (bien plus rapide et cliquables).
            Le WMS DIPUL raster a été retiré car il faisait doublon et ralentissait
            fortement le rendu (round-trip serveur par pan/zoom). */}
        <Section {...sectionProps} id="germany" flag="🇩🇪" name="Allemagne" active={countActive(showDfsAirspaceLayer)}>
          <Row label="Espace aérien (CTR/TMA)" checked={showDfsAirspaceLayer} onChange={setShowDfsAirspaceLayer} color={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', borderColor: '#4f46e5' }} testId="layer-toggle-dfs" />
          <Ed269Row cc="de" count={ed269Lookup.de} active={isEd269Active('de')} {...ed269RowProps} />
                </Section>

        {/* Belgium */}
        <Section {...sectionProps} id="belgium" flag="🇧🇪" name="Belgique" active={countActive(showBelgiumLayer, showBelgiumDroneRestrictions)}>
          <Row
            label="Zones drone nationales (NGI)"
            checked={showBelgiumLayer}
            onChange={setShowBelgiumLayer}
            color={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', borderColor: '#d97706' }}
            testId="layer-toggle-belgium"
          />
          <Row
            label={<span>Restriction drone <span className="text-[10px] font-mono text-slate-400">OpenAIP</span></span>}
            sub="158 zones · P/R/D/CTR"
            checked={showBelgiumDroneRestrictions}
            onChange={setShowBelgiumDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #dc2626, #f97316, #eab308, #2563eb)', borderColor: '#991b1b' }}
            testId="layer-toggle-belgium-drone-restrictions"
          />
          <Ed269Row cc="be" count={ed269Lookup.be} active={isEd269Active('be')} {...ed269RowProps} />
                </Section>

        {/* Netherlands */}
        <Section {...sectionProps} id="netherlands" flag="🇳🇱" name="Pays-Bas" active={countActive(showNetherlandsLayer, showNetherlandsDroneRestrictions)}>
          <Row label="Zones drones (LVNL)" checked={showNetherlandsLayer} onChange={setShowNetherlandsLayer} color={{ background: 'linear-gradient(135deg, #f97316, #c2410c)', borderColor: '#9a3412' }} testId="layer-toggle-netherlands" />
          <Row
            label={<span>Restriction drone <span className="text-[10px] font-mono text-slate-400">OpenAIP</span></span>}
            sub="62 zones · P/R/D/CTR"
            checked={showNetherlandsDroneRestrictions}
            onChange={setShowNetherlandsDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #dc2626, #f97316, #eab308, #2563eb)', borderColor: '#991b1b' }}
            testId="layer-toggle-netherlands-drone-restrictions"
          />
          <Ed269Row cc="nl" count={ed269Lookup.nl} active={isEd269Active('nl')} {...ed269RowProps} />
                </Section>

        {/* Luxembourg */}
        <Section {...sectionProps} id="luxembourg" flag="🇱🇺" name="Luxembourg" active={countActive(showLuxembourgLayer)}>
          <Row label="Zones drone (0-120m AGL)" checked={showLuxembourgLayer} onChange={setShowLuxembourgLayer} color={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)', borderColor: '#0e7490' }} testId="layer-toggle-luxembourg" />
          <Ed269Row cc="lu" count={ed269Lookup.lu} active={isEd269Active('lu')} {...ed269RowProps} />
                </Section>

        {/* Spain */}
        <Section {...sectionProps} id="spain" flag="🇪🇸" name="Espagne" active={countActive(showSpainLayer)}>
          <Row
            label="Zones drone nationales"
            checked={showSpainLayer}
            onChange={(v) => {
              setShowSpainLayer(v);
              if (v) {
                // Also enable the official ED-269/ED-318 vector layer for Spain
                // — the WMS alone draws very thin contours; the vector layer
                // colour-fills airport protection bands, sensitive infrastructure
                // and urban zones for a much clearer visual reading.
                // We add 'es' to the active Set without disturbing any
                // country the user might already have selected.
                setEd269Country((prev) => {
                  const next = new Set(prev || []);
                  next.add('es');
                  return next;
                });
                setShowEd269Layer(true);
                if (!showOpenAipLayer) setShowOpenAipLayer(true);
              }
            }}
            color={{ background: 'linear-gradient(135deg, #f97316, #ea580c)', borderColor: '#c2410c' }}
            testId="layer-toggle-spain"
          />
          <Ed269Row cc="es" count={ed269Lookup.es} active={isEd269Active('es')} {...ed269RowProps} />
                </Section>

        {/* Italy */}
        <Section {...sectionProps} id="italy" flag="🇮🇹" name="Italie" active={countActive(showItalyLayer) + countActive(showItalyDroneRestrictions)}>
          <Row
            label="Espace aérien drone"
            checked={showItalyLayer}
            onChange={(v) => { setShowItalyLayer(v); if (v && !showOpenAipLayer) setShowOpenAipLayer(true); }}
            color={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', borderColor: '#15803d' }}
            testId="layer-toggle-italy"
          />
          <Row
            label="Restriction drone"
            sub="P / R / D / CTR (OpenAIP)"
            checked={showItalyDroneRestrictions}
            onChange={setShowItalyDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #dc2626, #f97316, #eab308, #2563eb)', borderColor: '#7f1d1d' }}
            testId="layer-toggle-italy-drone-restrictions"
          />
          <Ed269Row cc="it" count={ed269Lookup.it} active={isEd269Active('it')} {...ed269RowProps} />
                </Section>

        {/* Switzerland */}
        <Section {...sectionProps} id="switzerland" flag="🇨🇭" name="Suisse" active={countActive(showSwitzerlandLayer)}>
          <Row label="Restrictions drones (carte nationale)" checked={showSwitzerlandLayer} onChange={setShowSwitzerlandLayer} color={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)', borderColor: '#7f1d1d' }} testId="layer-toggle-switzerland" />
          <Ed269Row cc="ch" count={ed269Lookup.ch} active={isEd269Active('ch')} {...ed269RowProps} />
                </Section>

        {/* Austria */}
        <Section {...sectionProps} id="austria" flag="🇦🇹" name="Autriche" active={countActive(showAustriaLayer)}>
          <Row label="Espace aérien réglementé" checked={showAustriaLayer} onChange={setShowAustriaLayer} color={{ background: 'linear-gradient(135deg, #f43f5e, #be123c)', borderColor: '#881337' }} testId="layer-toggle-austria" />
          <Ed269Row cc="at" count={ed269Lookup.at} active={isEd269Active('at')} {...ed269RowProps} />
                </Section>

        {/* Iceland */}
        <Section {...sectionProps} id="iceland" flag="🇮🇸" name="Islande" active={countActive(showIcelandLayer)}>
          <Row label="Zones drone nationales" checked={showIcelandLayer} onChange={setShowIcelandLayer} color={{ background: 'linear-gradient(135deg, #06b6d4, #0e7490)', borderColor: '#155e75' }} testId="layer-toggle-iceland" />
          <Ed269Row cc="is" count={ed269Lookup.is} active={isEd269Active('is')} {...ed269RowProps} />
                </Section>

        {/* Czechia (Czech Republic) */}
        <Section {...sectionProps} id="czechia" flag="🇨🇿" name="Tchéquie" active={countActive(showCzechiaLayer)}>
          <Row
            label="Zones drone nationales"
            checked={showCzechiaLayer}
            onChange={setShowCzechiaLayer}
            color={{ background: 'linear-gradient(135deg, #ef4444, #1d4ed8)', borderColor: '#7f1d1d' }}
            testId="layer-toggle-czechia"
          />
          <Ed269Row cc="cz" count={ed269Lookup.cz} active={isEd269Active('cz')} {...ed269RowProps} />
                </Section>

        {/* Slovakia */}
        <Section {...sectionProps} id="slovakia" flag="🇸🇰" name="Slovaquie" active={countActive(showSlovakiaLayer)}>
          <Row
            label="Espace aérien drone (273 zones)"
            checked={showSlovakiaLayer}
            onChange={setShowSlovakiaLayer}
            color={{ background: 'linear-gradient(135deg, #1d4ed8, #ef4444, #ffffff)', borderColor: '#7f1d1d' }}
            testId="layer-toggle-slovakia"
          />
          <Ed269Row cc="sk" count={ed269Lookup.sk} active={isEd269Active('sk')} {...ed269RowProps} />
                </Section>

        {/* Denmark */}
        <Section {...sectionProps} id="denmark" flag="🇩🇰" name="Danemark" active={countActive(showDenmarkLayer)}>
          <Row
            label="Zones drone nationales"
            checked={showDenmarkLayer}
            onChange={setShowDenmarkLayer}
            color={{ background: 'linear-gradient(135deg, #dc2626, #ffffff)', borderColor: '#7f1d1d' }}
            testId="layer-toggle-denmark"
          />
          <Ed269Row cc="dk" count={ed269Lookup.dk} active={isEd269Active('dk')} {...ed269RowProps} />
                </Section>

        {/* Ireland */}
        <Section {...sectionProps} id="ireland" flag="🇮🇪" name="Irlande" active={countActive(showIrelandLayer)}>
          <Row
            label="Zones drone nationales"
            checked={showIrelandLayer}
            onChange={setShowIrelandLayer}
            color={{ background: 'linear-gradient(135deg, #16a34a, #ffffff, #f97316)', borderColor: '#14532d' }}
            testId="layer-toggle-ireland"
          />
          <Ed269Row cc="ie" count={ed269Lookup.ie} active={isEd269Active('ie')} {...ed269RowProps} />
                </Section>

        {/* Estonia */}
        <Section {...sectionProps} id="estonia" flag="🇪🇪" name="Estonie" active={countActive(showEstoniaLayer)}>
          <Row
            label="Zones drone nationales"
            checked={showEstoniaLayer}
            onChange={setShowEstoniaLayer}
            color={{ background: 'linear-gradient(180deg, #1e40af, #000000, #ffffff)', borderColor: '#1e3a8a' }}
            testId="layer-toggle-estonia"
          />
          <Ed269Row cc="ee" count={ed269Lookup.ee} active={isEd269Active('ee')} {...ed269RowProps} />
                </Section>

        {/* Cyprus */}
        <Section {...sectionProps} id="cyprus" flag="🇨🇾" name="Chypre" active={countActive(showCyprusLayer)}>
          <Row
            label="Zones drone nationales"
            checked={showCyprusLayer}
            onChange={setShowCyprusLayer}
            color={{ background: 'linear-gradient(135deg, #f59e0b, #ffffff, #047857)', borderColor: '#92400e' }}
            testId="layer-toggle-cyprus"
          />
          <Ed269Row cc="cy" count={ed269Lookup.cy} active={isEd269Active('cy')} {...ed269RowProps} />
                </Section>

        {/* Portugal */}
        <Section {...sectionProps} id="portugal" flag="🇵🇹" name="Portugal" active={countActive(isEd269Active('pt'))}>
          <Ed269Row cc="pt" count={ed269Lookup.pt} active={isEd269Active('pt')} {...ed269RowProps} />
                </Section>

        {/* Sweden */}
        <Section {...sectionProps} id="sweden" flag="🇸🇪" name="Suède" active={countActive(showSwedenDroneRestrictions)}>
          <Row
            label="Restriction drone"
            sub="R / D / CTR (OpenAIP)"
            checked={showSwedenDroneRestrictions}
            onChange={setShowSwedenDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #f97316, #eab308, #2563eb)', borderColor: '#9a3412' }}
            testId="layer-toggle-sweden-drone-restrictions"
          />
                </Section>

        {/* Romania */}
        <Section {...sectionProps} id="romania" flag="🇷🇴" name="Roumanie" active={countActive(isEd269Active('ro'))}>
          <Ed269Row cc="ro" count={ed269Lookup.ro} active={isEd269Active('ro')} {...ed269RowProps} />
                </Section>

        {/* Finland */}
        <Section {...sectionProps} id="finland" flag="🇫🇮" name="Finlande" active={countActive(showFinlandDroneRestrictions)}>
          <Row
            label="Restriction drone"
            sub="R / D / CTR (OpenAIP)"
            checked={showFinlandDroneRestrictions}
            onChange={setShowFinlandDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #f97316, #eab308, #2563eb)', borderColor: '#9a3412' }}
            testId="layer-toggle-finland-drone-restrictions"
          />
                </Section>

        {/* Poland */}
        <Section {...sectionProps} id="poland" flag="🇵🇱" name="Pologne" active={countActive(showPolandDroneRestrictions)}>
          <Row
            label={<span>Restriction drone <span className="text-[10px] font-mono text-slate-400">OpenAIP</span></span>}
            sub="119 zones · P/R/D/CTR"
            checked={showPolandDroneRestrictions}
            onChange={setShowPolandDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #dc2626, #f97316, #eab308, #2563eb)', borderColor: '#991b1b' }}
            testId="layer-toggle-poland-drone-restrictions"
          />
                </Section>

        {/* Norway */}
        <Section {...sectionProps} id="norway" flag="🇳🇴" name="Norvège" active={countActive(showNorwayDroneRestrictions)}>
          <Row
            label={<span>Restriction drone <span className="text-[10px] font-mono text-slate-400">OpenAIP</span></span>}
            sub="120 zones · R/D/CTR"
            checked={showNorwayDroneRestrictions}
            onChange={setShowNorwayDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #f97316, #eab308, #2563eb)', borderColor: '#9a3412' }}
            testId="layer-toggle-norway-drone-restrictions"
          />
                </Section>

        {/* Croatia */}
        <Section {...sectionProps} id="croatia" flag="🇭🇷" name="Croatie" active={countActive(showCroatiaDroneRestrictions)}>
          <Row
            label={<span>Restriction drone <span className="text-[10px] font-mono text-slate-400">OpenAIP</span></span>}
            sub="124 zones · P/R/D/CTR"
            checked={showCroatiaDroneRestrictions}
            onChange={setShowCroatiaDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #dc2626, #f97316, #eab308, #2563eb)', borderColor: '#991b1b' }}
            testId="layer-toggle-croatia-drone-restrictions"
          />
                </Section>

        {/* Greece */}
        <Section {...sectionProps} id="greece" flag="🇬🇷" name="Grèce" active={countActive(showGreeceDroneRestrictions)}>
          <Row
            label={<span>Restriction drone <span className="text-[10px] font-mono text-slate-400">OpenAIP</span></span>}
            sub="153 zones · P/R/D/CTR"
            checked={showGreeceDroneRestrictions}
            onChange={setShowGreeceDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #dc2626, #f97316, #eab308, #2563eb)', borderColor: '#991b1b' }}
            testId="layer-toggle-greece-drone-restrictions"
          />
                </Section>

        {/* Bulgaria */}
        <Section {...sectionProps} id="bulgaria" flag="🇧🇬" name="Bulgarie" active={countActive(showBulgariaDroneRestrictions)}>
          <Row
            label={<span>Restriction drone <span className="text-[10px] font-mono text-slate-400">OpenAIP</span></span>}
            sub="143 zones · P/R/D/CTR"
            checked={showBulgariaDroneRestrictions}
            onChange={setShowBulgariaDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #dc2626, #f97316, #eab308, #2563eb)', borderColor: '#991b1b' }}
            testId="layer-toggle-bulgaria-drone-restrictions"
          />
                </Section>

        {/* Hungary */}
        <Section {...sectionProps} id="hungary" flag="🇭🇺" name="Hongrie" active={countActive(showHungaryDroneRestrictions)}>
          <Row
            label={<span>Restriction drone <span className="text-[10px] font-mono text-slate-400">OpenAIP</span></span>}
            sub="43 zones · P/R/D/CTR"
            checked={showHungaryDroneRestrictions}
            onChange={setShowHungaryDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #dc2626, #f97316, #eab308, #2563eb)', borderColor: '#991b1b' }}
            testId="layer-toggle-hungary-drone-restrictions"
          />
                </Section>

        {/* Slovenia */}
        <Section {...sectionProps} id="slovenia" flag="🇸🇮" name="Slovénie" active={countActive(showSloveniaDroneRestrictions)}>
          <Row
            label={<span>Restriction drone <span className="text-[10px] font-mono text-slate-400">OpenAIP</span></span>}
            sub="12 zones · P/R/D/CTR"
            checked={showSloveniaDroneRestrictions}
            onChange={setShowSloveniaDroneRestrictions}
            color={{ background: 'linear-gradient(135deg, #dc2626, #f97316, #eab308, #2563eb)', borderColor: '#991b1b' }}
            testId="layer-toggle-slovenia-drone-restrictions"
          />
                </Section>


        {/* DJI global */}
        <Section {...sectionProps} id="dji" flag="🌐" name="Restrictions firmware" active={countActive(showDjiLayer)}>
          <Row label="Géofencing constructeur" checked={showDjiLayer} onChange={setShowDjiLayer} color={{ background: 'linear-gradient(135deg, #ef4444, #fbbf24, #3b82f6)', borderColor: '#374151' }} testId="layer-toggle-dji" />
        </Section>

        {/* Environnement */}
        <Section {...sectionProps} id="environment" flag={<Leaf className="w-4 h-4 text-emerald-600 inline" />} name="Environnement" active={countActive(showNatura2000Wms, showEnvironmentalLayers?.reserve_naturelle, showEnvironmentalLayers?.parc_national, showEnvironmentalLayers?.parc_regional)}>
          <Row label="🇪🇺 Espaces naturels protégés" checked={showNatura2000Wms} onChange={setShowNatura2000Wms} color={{ backgroundColor: 'rgba(16, 185, 129, 0.4)', borderColor: '#10b981' }} testId="layer-toggle-natura2000" />
          <Row label="🇫🇷 Réserves naturelles" checked={showEnvironmentalLayers?.reserve_naturelle} onChange={(v) => setShowEnvironmentalLayers((p) => ({ ...p, reserve_naturelle: v }))} color={{ backgroundColor: 'rgba(234, 179, 8, 0.4)', borderColor: '#eab308' }} testId="layer-toggle-reserves" />
          <Row label="🇫🇷 Parcs nationaux" checked={showEnvironmentalLayers?.parc_national} onChange={(v) => setShowEnvironmentalLayers((p) => ({ ...p, parc_national: v }))} color={{ backgroundColor: 'rgba(220, 38, 38, 0.3)', borderColor: '#dc2626' }} testId="layer-toggle-parcs-nat" />
          <Row label="🇫🇷 Parcs régionaux" checked={showEnvironmentalLayers?.parc_regional} onChange={(v) => setShowEnvironmentalLayers((p) => ({ ...p, parc_regional: v }))} color={{ backgroundColor: 'rgba(249, 115, 22, 0.3)', borderColor: '#f97316' }} testId="layer-toggle-parcs-reg" />
          {loadingEnvZones && <p className="text-[10px] text-slate-400 px-2 py-1">Chargement…</p>}
        </Section>

        {/* POI */}
        <Section {...sectionProps} id="poi" flag={<Building2 className="w-4 h-4 text-rose-500 inline" />} name="Points d'intérêt" active={countActive(showHospitalsLayer, showAirportsLayer, showAirportsPPPLayer, showHelistationsLayer)}>
          <Row label="🏥 Hôpitaux (Europe)" checked={showHospitalsLayer} onChange={setShowHospitalsLayer} color={{ backgroundColor: '#ef4444', borderColor: '#dc2626' }} testId="layer-toggle-hospitals" />
          <Row label="🚁 Hélistations (Europe)" checked={showHelistationsLayer} onChange={setShowHelistationsLayer} color={{ backgroundColor: '#f97316', borderColor: '#ea580c' }} testId="layer-toggle-helistations" />
          <Row label="✈️ Aéroports / aérodromes" sub="🇫🇷" checked={showAirportsLayer} onChange={setShowAirportsLayer} color={{ backgroundColor: '#3b82f6', borderColor: '#2563eb' }} testId="layer-toggle-airports" />
          <Row label="📍 Périmètres de protection" sub="🇫🇷" checked={showAirportsPPPLayer} onChange={setShowAirportsPPPLayer} color={{ backgroundColor: '#60a5fa', borderColor: '#2563eb' }} testId="layer-toggle-airports-ppp" />
        </Section>
      </div>

      {/* Footer — quick actions */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-slate-200 bg-slate-50">
        <button
          onClick={() => {
            setShowIgnLayer(true);
            setShowDipulLayer(true);
            setShowDfsAirspaceLayer(true);
            setShowBelgiumLayer(true);
            setShowLuxembourgLayer(true);
            setShowNetherlandsLayer(true);
            setShowSwitzerlandLayer(true);
            setShowAustriaLayer(true);
            setShowIcelandLayer(true);
            setShowCzechiaLayer(true);
            setShowSlovakiaLayer(true);
            setShowDenmarkLayer(true);
            setShowIrelandLayer(true);
            setShowEstoniaLayer(true);
            setShowCyprusLayer(true);
            setShowSpainLayer(true);
            setShowItalyLayer(true);
            setShowDjiLayer(true);
            setShowNatura2000Wms(true);
            setShowHospitalsLayer(true);
            setShowAirportsLayer(true);
            setShowHelistationsLayer(true);
            setLayerVisibility({ ctr: true, tma: true, restricted: true, prohibited: true, danger: true });
            setShowEnvironmentalLayers({ natura2000_habitat: true, natura2000_oiseaux: true, reserve_naturelle: true, parc_national: true, parc_regional: true });
          }}
          className="flex-1 px-2 py-1 text-xs text-sky-600 hover:bg-sky-50 rounded border border-sky-200"
          data-testid="layers-enable-all"
        >
          Tout activer
        </button>
        <button
          onClick={() => {
            setShowIgnLayer(false);
            setShowDipulLayer(false);
            setShowDfsAirspaceLayer(false);
            setShowBelgiumLayer(false);
            setShowLuxembourgLayer(false);
            setShowNetherlandsLayer(false);
            setShowSwitzerlandLayer(false);
            setShowAustriaLayer(false);
            setShowIcelandLayer(false);
            setShowCzechiaLayer(false);
            setShowSlovakiaLayer(false);
            setShowDenmarkLayer(false);
            setShowIrelandLayer(false);
            setShowEstoniaLayer(false);
            setShowCyprusLayer(false);
            setShowItalyLayer(false);
            setShowSpainLayer(false);
            setShowOpenAipLayer(false);
            setShowEd269Layer(false);
            setShowDjiLayer(false);
            setShowNatura2000Wms(false);
            setShowHospitalsLayer(false);
            setShowAirportsLayer(false);
            setShowAirportsPPPLayer(false);
            setShowHelistationsLayer(false);
            setLayerVisibility({ ctr: false, tma: false, restricted: false, prohibited: false, danger: false });
            setShowEnvironmentalLayers({ natura2000_habitat: false, natura2000_oiseaux: false, reserve_naturelle: false, parc_national: false, parc_regional: false });
          }}
          className="flex-1 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded border border-slate-200"
          data-testid="layers-disable-all"
        >
          Tout désactiver
        </button>
      </div>
    </div>
  );
}
